'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Moon, Sun } from 'lucide-react'
import { flushSync } from 'react-dom'
import { cn } from '@/lib/utils'
import { ScrollTooltip } from '@/components/ui/ScrollTooltip'

interface AnimatedThemeToggleProps extends React.ComponentPropsWithoutRef<'button'> {
  duration?: number
  showScrollTooltip?: boolean
}

export const AnimatedThemeToggle = ({
  className,
  duration = 400,
  showScrollTooltip = true,
  ...props
}: AnimatedThemeToggleProps) => {
  const [isDark, setIsDark] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const updateTheme = () => {
      const currentTheme = document.documentElement.getAttribute('data-theme')
      setIsDark(currentTheme === 'dark')
    }

    updateTheme()

    const observer = new MutationObserver(updateTheme)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })

    return () => observer.disconnect()
  }, [])

  const toggleTheme = useCallback(async () => {
    if (!buttonRef.current) return

    // Check if View Transitions API is supported
    if (!document.startViewTransition) {
      // Fallback for browsers that don't support View Transitions
      const newTheme = isDark ? 'light' : 'dark'
      setIsDark(!isDark)
      localStorage.setItem('theme', newTheme)
      document.documentElement.setAttribute('data-theme', newTheme)
      return
    }

    await document.startViewTransition(() => {
      flushSync(() => {
        const newTheme = isDark ? 'light' : 'dark'
        setIsDark(!isDark)
        localStorage.setItem('theme', newTheme)
        document.documentElement.setAttribute('data-theme', newTheme)
      })
    }).ready

    const { top, left, width, height } =
      buttonRef.current.getBoundingClientRect()
    const x = left + width / 2
    const y = top + height / 2
    const maxRadius = Math.hypot(
      Math.max(left, window.innerWidth - left),
      Math.max(top, window.innerHeight - top)
    )

    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${x}px ${y}px)`,
          `circle(${maxRadius}px at ${x}px ${y}px)`,
        ],
      },
      {
        duration,
        easing: 'ease-in-out',
        pseudoElement: '::view-transition-new(root)',
      }
    )
  }, [isDark, duration])

  const button = (
    <button
      ref={buttonRef}
      onClick={toggleTheme}
      className={cn(
        'p-2 transition-colors duration-200 rounded-full hover:bg-muted focus-visible:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary text-foreground',
        className
      )}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} theme`}
      {...props}
    >
      {isDark ? <Sun className='w-5 h-5' /> : <Moon className='w-5 h-5' />}
      <span className='sr-only'>Toggle theme</span>
    </button>
  )

  if (showScrollTooltip) {
    return (
      <ScrollTooltip
        content='Switch modes'
        scrollThreshold={200}
        autoDismissDelay={5000}
        storageKey='theme-scroll-tooltip-seen'
      >
        {button}
      </ScrollTooltip>
    )
  }

  return button
}
